import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { caseStudiesData } from "@/mocks/caseStudiesData";
import { useSEO } from "@/hooks/useSEO";

const allIndustries = ["All", ...Array.from(new Set(caseStudiesData.map((c) => c.industry)))];

export default function CaseStudies() {
  useSEO({
    title: "Case Studies & Success Stories Malaysia | SE Distribution Enterprise Deployments",
    description: "Real-world enterprise deployments across Malaysia — body-worn cameras for law enforcement, HCI migration from VMware, rugged computing for logistics. 500+ clients, 98% retention rate.",
    canonical: "https://www.sedistribution.com.my/case-studies",
    keywords: "SE Distribution case studies, enterprise deployment Malaysia, body camera deployment Malaysia, VMware migration Malaysia, rugged computing deployment Malaysia",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sedistribution.com.my/" },
          { "@type": "ListItem", "position": 2, "name": "Case Studies", "item": "https://www.sedistribution.com.my/case-studies" }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Case Studies & Success Stories Malaysia | SE Distribution",
        "description": "Real-world enterprise deployments across Malaysia — body-worn cameras for law enforcement, VMware migration, rugged computing for logistics, and enterprise backup. 500+ clients served.",
        "url": "https://www.sedistribution.com.my/case-studies",
        "inLanguage": "en-MY",
        "publisher": {
          "@type": "Organization",
          "name": "SE Distribution Sdn Bhd",
          "url": "https://www.sedistribution.com.my"
        }
      }
    ],
  });
  const [filter, setFilter] = useState("All");
  const [activeCase, setActiveCase] = useState<string | null>(null);

  const filtered = filter === "All" ? caseStudiesData : caseStudiesData.filter((c) => c.industry === filter);
  const detail = caseStudiesData.find((c) => c.id === activeCase);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        {/* Hero */}
        <div className="relative bg-[#1a1a1a] py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20business%20team%20celebrating%20success%20in%20modern%20office%2C%20diverse%20Malaysian%20professionals%2C%20achievement%20and%20results%2C%20clean%20corporate%20environment%2C%20warm%20lighting%2C%20photorealistic%20commercial%20photography&width=1400&height=500&seq=cs-hero1&orientation=landscape"
            alt="Case Studies"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/70 via-[#1a1a1a]/50 to-[#1a1a1a]/80"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <span className="text-xs font-semibold tracking-widest uppercase text-white/50 mb-3 block">
              Proven Results
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
              Case Studies &amp; <strong>Success Stories</strong>
            </h1>
            <p className="text-white/70 text-sm max-w-xl mx-auto leading-relaxed">
              Real organizations. Real challenges. Measurable outcomes. Explore how SE Distribution has helped clients across Malaysia achieve transformative results with our technology solutions.
            </p>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-[#f9f7f4] border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
              {[
                { value: "500+", label: "Clients Served" },
                { value: "6", label: "Industries Covered" },
                { value: "6", label: "Brand Partners" },
                { value: "12+", label: "Years of Experience" },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-2xl font-bold text-[#1a1a1a]">{s.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex items-center gap-2 flex-wrap">
            {allIndustries.map((ind) => (
              <button
                key={ind}
                onClick={() => setFilter(ind)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  filter === ind
                    ? "bg-[#1a1a1a] text-white"
                    : "bg-[#f9f7f4] text-gray-500 hover:text-[#1a1a1a] hover:bg-gray-100"
                }`}
              >
                {ind}
              </button>
            ))}
          </div>
        </div>

        {/* Case Studies Grid */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filtered.map((cs) => (
              <div
                key={cs.id}
                className="border border-gray-100 rounded-xl overflow-hidden hover:border-[#1a1a1a]/20 transition-all cursor-pointer group"
                onClick={() => setActiveCase(cs.id)}
              >
                {/* Image */}
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={cs.image}
                    alt={cs.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className={`${cs.industryIcon} text-gray-400 text-sm`}></i>
                      </div>
                      <span className="text-xs font-semibold tracking-widest uppercase text-gray-400">{cs.industry}</span>
                    </div>
                    <span className="text-xs bg-[#1a1a1a]/5 text-[#1a1a1a] px-2.5 py-1 rounded-full font-medium">{cs.brand}</span>
                  </div>
                  <h3 className="font-bold text-[#1a1a1a] text-base mb-3 leading-snug">{cs.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5 line-clamp-2">{cs.challenge}</p>

                  {/* Results Preview */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {cs.results.slice(0, 2).map((r) => (
                      <div key={r.label} className="bg-[#f9f7f4] rounded-lg p-3 text-center">
                        <p className="text-lg font-bold text-[#1a1a1a]">{r.metric}</p>
                        <p className="text-xs text-gray-500 mt-0.5 leading-tight">{r.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 text-sm font-medium text-[#1a1a1a] group-hover:gap-3 transition-all">
                    <span>Read Full Story</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#f9f7f4] py-16">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-4">
              Ready to Write Your Own Success Story?
            </h2>
            <p className="text-gray-500 text-sm mb-8 leading-relaxed">
              Join hundreds of Malaysian organizations that trust SE Distribution for their technology needs. Let us design a solution tailored to your specific challenges.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Link
                to="/contact"
                className="bg-[#1a1a1a] text-white text-sm font-semibold px-8 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
              >
                Start a Conversation
              </Link>
              <a
                href="tel:+60364127917"
                className="border border-[#1a1a1a] text-[#1a1a1a] text-sm font-medium px-8 py-3 rounded-md hover:bg-[#1a1a1a] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                +603 6412 7917
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Case Study Detail Modal */}
      {detail && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-8"
          onClick={() => setActiveCase(null)}
        >
          <div
            className="bg-white rounded-xl max-w-3xl w-full mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image */}
            <div className="relative w-full h-56 overflow-hidden">
              <img src={detail.image} alt={detail.title} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/70 to-transparent"></div>
              <button
                onClick={() => setActiveCase(null)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/40 transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
              <div className="absolute bottom-4 left-6">
                <span className="text-xs font-semibold tracking-widest uppercase text-white/70">{detail.industry}</span>
                <h2 className="text-xl font-bold text-white mt-1 leading-snug">{detail.title}</h2>
              </div>
            </div>

            <div className="p-8">
              {/* Meta */}
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="flex items-center gap-1.5 text-xs text-gray-500 bg-[#f9f7f4] px-3 py-1.5 rounded-full">
                  <i className="ri-map-pin-line"></i> {detail.location}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-gray-500 bg-[#f9f7f4] px-3 py-1.5 rounded-full">
                  <i className="ri-time-line"></i> {detail.duration}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-gray-500 bg-[#f9f7f4] px-3 py-1.5 rounded-full">
                  <i className="ri-award-line"></i> {detail.brand}
                </span>
              </div>

              {/* Challenge */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wide mb-2">The Challenge</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{detail.challenge}</p>
              </div>

              {/* Solution */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wide mb-2">Our Solution</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{detail.solution}</p>
              </div>

              {/* Results */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wide mb-3">Measurable Results</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {detail.results.map((r) => (
                    <div key={r.label} className="bg-[#f9f7f4] rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-[#1a1a1a]">{r.metric}</p>
                      <p className="text-xs text-gray-500 mt-1 leading-tight">{r.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quote */}
              <div className="bg-[#1a1a1a] rounded-lg p-5 mb-6">
                <i className="ri-double-quotes-l text-white/30 text-2xl mb-2 block"></i>
                <p className="text-white/90 text-sm leading-relaxed italic mb-3">{detail.quote}</p>
                <div>
                  <p className="text-white text-sm font-semibold">{detail.quoteName}</p>
                  <p className="text-white/50 text-xs">{detail.quoteTitle}</p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {detail.tags.map((t) => (
                  <span key={t} className="text-xs bg-[#f9f7f4] text-gray-500 px-3 py-1 rounded-full">{t}</span>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <Link
                  to="/contact"
                  onClick={() => setActiveCase(null)}
                  className="bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-2.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Discuss a Similar Project
                </Link>
                <button
                  onClick={() => setActiveCase(null)}
                  className="text-sm text-gray-500 hover:text-[#1a1a1a] transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
